import { useEffect, useState, type ReactNode } from 'react';
import { Plus, X, Pencil, Trash2, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import Button from '../ui/Button';
import { equipmentService } from '../../services/equipment';
import type {
  EquipmentCategory, EquipmentTypeInput, ManagedEquipmentType, MeterType,
} from '../../types/equipment';
import { EQUIPMENT_CATEGORIES, categoryLabel } from '../../types/equipment';

const errorMessage = (err: unknown, fallback: string) =>
  err instanceof Error && err.message ? err.message : fallback;

// Pre-fill only — what a new type of each category usually starts as. The
// server applies the same defaults when none are sent; every field stays
// editable here and per machine.
const CATEGORY_DEFAULTS: Record<EquipmentCategory, Pick<EquipmentTypeInput, 'default_meter_type' | 'default_is_implement' | 'default_can_tow'>> = {
  vehicle:   { default_meter_type: 'km',    default_is_implement: false, default_can_tow: false },
  machinery: { default_meter_type: 'hours', default_is_implement: false, default_can_tow: true },
  implement: { default_meter_type: 'none',  default_is_implement: true,  default_can_tow: false },
  sprayer:   { default_meter_type: 'none',  default_is_implement: false, default_can_tow: false },
  pump:      { default_meter_type: 'hours', default_is_implement: false, default_can_tow: false },
  handheld:  { default_meter_type: 'none',  default_is_implement: false, default_can_tow: false },
  plant:     { default_meter_type: 'hours', default_is_implement: false, default_can_tow: false },
  other:     { default_meter_type: 'none',  default_is_implement: false, default_can_tow: false },
};

const METER_LABELS: Record<MeterType, string> = {
  hours: 'Hours', km: 'Kilometres', both: 'Hours and km', none: 'No meter',
};

const inputClass = 'w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-green-500';

function usage(t: ManagedEquipmentType): string {
  const parts: string[] = [];
  if (t.machine_count) parts.push(`${t.machine_count} machine${t.machine_count === 1 ? '' : 's'}`);
  if (t.template_count) parts.push(`${t.template_count} checklist${t.template_count === 1 ? '' : 's'}`);
  return parts.length ? parts.join(' · ') : 'Not used';
}

export default function EquipmentTypesManager() {
  const [types, setTypes]         = useState<ManagedEquipmentType[]>([]);
  const [loading, setLoading]     = useState(true);
  const [pageError, setPageError] = useState<string | null>(null);
  const [showBuiltins, setShowBuiltins] = useState(false);
  const [editing, setEditing]     = useState<ManagedEquipmentType | 'new' | null>(null);
  const [deleting, setDeleting]   = useState<ManagedEquipmentType | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await equipmentService.listManagedTypes();
      if (res.success && res.data) setTypes(res.data);
      setPageError(null);
    } catch (err) {
      setPageError(errorMessage(err, 'Could not load equipment types'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const custom    = types.filter(t => !t.is_builtin && !t.shadows_builtin);
  const leftovers = types.filter(t => t.shadows_builtin);
  const builtins  = types.filter(t => t.is_builtin);

  return (
    <div className="space-y-4">
      {pageError && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{pageError}</p>
      )}

      {loading ? (
        <div className="space-y-2">
          {[0, 1, 2].map(i => <div key={i} className="h-14 bg-white rounded-xl border border-neutral-100 animate-pulse" />)}
        </div>
      ) : (
        <>
          {leftovers.length > 0 && (
            <section className="border border-amber-200 bg-amber-50 rounded-xl p-4 space-y-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <h3 className="text-sm font-semibold text-amber-900">Duplicates of built-in types</h3>
                  <p className="text-xs text-amber-800 mt-0.5">
                    These were created as custom types with the same name as a built-in one, before that was
                    prevented. Delete them — their machines and checklists carry on under the built-in type.
                  </p>
                </div>
              </div>
              <div className="space-y-1.5">
                {leftovers.map(t => (
                  <div key={t.type_key} className="flex items-center gap-3 bg-white border border-amber-100 rounded-lg px-3 py-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-neutral-800 break-words">{t.label}</p>
                      <p className="text-xs text-neutral-500">{usage(t)}</p>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => setDeleting(t)}>Delete</Button>
                  </div>
                ))}
              </div>
            </section>
          )}

          <section className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <h3 className="text-sm font-semibold text-neutral-800">Your types</h3>
                <p className="text-xs text-neutral-500">Types your organisation added. Rename, recategorise or delete them here.</p>
              </div>
              <Button size="sm" onClick={() => setEditing('new')} className="flex-shrink-0">
                <Plus className="h-4 w-4 mr-1" />
                Add type
              </Button>
            </div>
            {custom.length === 0 ? (
              <p className="text-sm text-neutral-400 text-center py-6 border border-dashed border-neutral-200 rounded-lg">
                No custom types yet
              </p>
            ) : (
              <div className="space-y-1.5">
                {custom.map(t => (
                  <div key={t.type_key} className="flex items-center gap-3 bg-white border border-neutral-200 rounded-lg px-3 py-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-neutral-800 break-words">{t.label}</p>
                      <p className="text-xs text-neutral-500">
                        {categoryLabel(t.category)} · {usage(t)}
                      </p>
                    </div>
                    <button
                      onClick={() => setEditing(t)}
                      aria-label={`Edit ${t.label}`}
                      className="p-1.5 text-neutral-400 hover:text-green-600 rounded hover:bg-green-50 flex-shrink-0"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setDeleting(t)}
                      aria-label={`Delete ${t.label}`}
                      className="p-1.5 text-neutral-400 hover:text-red-600 rounded hover:bg-red-50 flex-shrink-0"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="border border-neutral-200 rounded-xl bg-white">
            <button
              onClick={() => setShowBuiltins(v => !v)}
              className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left"
            >
              <div className="min-w-0">
                <h3 className="text-sm font-semibold text-neutral-800">Built-in types ({builtins.length})</h3>
                <p className="text-xs text-neutral-500">Shared by every organisation, so they cannot be changed here.</p>
              </div>
              {showBuiltins ? <ChevronUp className="h-4 w-4 text-neutral-400 flex-shrink-0" /> : <ChevronDown className="h-4 w-4 text-neutral-400 flex-shrink-0" />}
            </button>
            {showBuiltins && (
              <div className="border-t border-neutral-100 px-4 py-3 space-y-3">
                {EQUIPMENT_CATEGORIES.map(c => {
                  const inCat = builtins.filter(t => t.category === c.key);
                  if (!inCat.length) return null;
                  return (
                    <div key={c.key}>
                      <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-1">{c.label}</p>
                      <ul className="space-y-0.5">
                        {inCat.map(t => (
                          <li key={t.type_key} className="flex items-baseline justify-between gap-3 text-sm">
                            <span className="text-neutral-700 min-w-0 break-words">{t.label}</span>
                            <span className="text-xs text-neutral-400 flex-shrink-0">{usage(t)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </>
      )}

      {editing !== null && (
        <TypeEditModal
          type={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      {deleting && (
        <TypeDeleteModal
          type={deleting}
          targets={types.filter(t => t.type_key !== deleting.type_key && !t.shadows_builtin)}
          onClose={() => setDeleting(null)}
          onDeleted={() => { setDeleting(null); load(); }}
        />
      )}
    </div>
  );
}

// ─── Modals ──────────────────────────────────────────────────────────────────

function Modal({ title, onClose, children, footer }: {
  title: string; onClose: () => void; children: ReactNode; footer: ReactNode;
}) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div role="dialog" aria-label={title} className="bg-white rounded-xl shadow-xl max-w-md w-full my-4">
        <div className="px-5 py-4 border-b border-neutral-200 flex items-center justify-between gap-2">
          <h3 className="text-base font-semibold text-neutral-800 min-w-0 break-words">{title}</h3>
          <button onClick={onClose} aria-label="Close" className="text-neutral-400 hover:text-neutral-600 flex-shrink-0">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="px-5 py-4 space-y-4">{children}</div>
        <div className="px-5 py-4 border-t border-neutral-200 flex flex-wrap justify-end gap-2">{footer}</div>
      </div>
    </div>
  );
}

function TypeEditModal({ type, onClose, onSaved }: {
  type: ManagedEquipmentType | null; onClose: () => void; onSaved: () => void;
}) {
  const [draft, setDraft] = useState<EquipmentTypeInput>(() => type
    ? {
        label: type.label,
        category: type.category,
        default_meter_type: type.default_meter_type ?? 'none',
        default_is_implement: !!type.default_is_implement,
        default_can_tow: !!type.default_can_tow,
      }
    : { label: '', category: 'other', ...CATEGORY_DEFAULTS.other });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState<string | null>(null);

  // A new type's defaults follow its category until somebody sets them; an
  // existing type's are left alone, since they may have been chosen on purpose.
  const setCategory = (category: EquipmentCategory) =>
    setDraft(d => type ? { ...d, category } : { ...d, category, ...CATEGORY_DEFAULTS[category] });

  const save = async () => {
    if (!draft.label.trim()) { setError('Give the type a name'); return; }
    setSaving(true);
    setError(null);
    try {
      const res = type
        ? await equipmentService.updateType(type.type_key, draft)
        : await equipmentService.createType(draft);
      if (res.success) onSaved();
      else setError(res.error?.message || 'Save failed');
    } catch (err) {
      setError(errorMessage(err, 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={type ? `Edit ${type.label}` : 'New equipment type'}
      onClose={onClose}
      footer={<>
        <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
        <Button size="sm" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : type ? 'Save changes' : 'Add type'}
        </Button>
      </>}
    >
      <div>
        <label htmlFor="type-label" className="block text-xs font-medium text-neutral-600 mb-1">Name *</label>
        <input
          id="type-label"
          type="text"
          value={draft.label}
          maxLength={100}
          onChange={e => setDraft(d => ({ ...d, label: e.target.value }))}
          placeholder="e.g. Mulcher"
          className={inputClass}
        />
      </div>
      <div>
        <label htmlFor="type-category" className="block text-xs font-medium text-neutral-600 mb-1">Kind of equipment</label>
        <select
          id="type-category"
          value={draft.category}
          onChange={e => setCategory(e.target.value as EquipmentCategory)}
          className={inputClass}
        >
          {EQUIPMENT_CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
        <p className="text-xs text-neutral-400 mt-1">
          Decides which tab it lists under, and whether it gets a sprayer calibration or road-vehicle details.
        </p>
      </div>
      <fieldset className="space-y-2">
        <legend className="text-xs font-medium text-neutral-600 mb-1">A new machine of this type starts with</legend>
        <select
          aria-label="Default meter"
          value={draft.default_meter_type}
          onChange={e => setDraft(d => ({ ...d, default_meter_type: e.target.value as MeterType }))}
          className={inputClass}
        >
          {(Object.keys(METER_LABELS) as MeterType[]).map(m => <option key={m} value={m}>{METER_LABELS[m]}</option>)}
        </select>
        <label className="flex items-center gap-2 text-sm text-neutral-700">
          <input
            type="checkbox"
            checked={draft.default_is_implement}
            onChange={e => setDraft(d => ({ ...d, default_is_implement: e.target.checked }))}
            className="rounded"
          />
          Is an implement (towed or mounted)
        </label>
        <label className="flex items-center gap-2 text-sm text-neutral-700">
          <input
            type="checkbox"
            checked={draft.default_can_tow}
            onChange={e => setDraft(d => ({ ...d, default_can_tow: e.target.checked }))}
            className="rounded"
          />
          Can tow implements
        </label>
        <p className="text-xs text-neutral-400">Only a starting point — each machine can still be changed.</p>
      </fieldset>
      {error && <p className="text-sm text-red-600">{error}</p>}
    </Modal>
  );
}

function TypeDeleteModal({ type, targets, onClose, onDeleted }: {
  type: ManagedEquipmentType;
  targets: ManagedEquipmentType[];
  onClose: () => void;
  onDeleted: () => void;
}) {
  // A leftover copy of a built-in moves nothing: its machines keep the same
  // key, which then resolves to the built-in.
  const needsTarget = !type.shadows_builtin && type.machine_count + type.template_count > 0;
  const [target, setTarget]     = useState('');
  const [deleting, setDeleting] = useState(false);
  const [error, setError]       = useState<string | null>(null);

  const remove = async () => {
    if (needsTarget && !target) { setError('Choose a type to move them to'); return; }
    setDeleting(true);
    setError(null);
    try {
      const res = await equipmentService.deleteType(type.type_key, needsTarget ? target : undefined);
      if (res.success) onDeleted();
      else setError(res.error?.message || 'Delete failed');
    } catch (err) {
      setError(errorMessage(err, 'Delete failed'));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Modal
      title={`Delete ${type.label}?`}
      onClose={onClose}
      footer={<>
        <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
        <Button variant="danger" size="sm" onClick={remove} disabled={deleting || (needsTarget && !target)}>
          {deleting ? 'Deleting…' : needsTarget ? 'Move and delete' : 'Delete type'}
        </Button>
      </>}
    >
      {type.shadows_builtin ? (
        <p className="text-sm text-neutral-700">
          This duplicates the built-in {type.label} type. Its {usage(type).toLowerCase()} will carry on under the
          built-in one, filed as {categoryLabel(builtinCategory(type, targets))}.
        </p>
      ) : needsTarget ? (
        <>
          <p className="text-sm text-neutral-700">
            {type.label} is used by {usage(type).toLowerCase()}. Choose the type to move them to — the machines keep
            everything else, including their meter readings and history.
          </p>
          <div>
            <label htmlFor="reassign-to" className="block text-xs font-medium text-neutral-600 mb-1">Move them to</label>
            <select id="reassign-to" value={target} onChange={e => setTarget(e.target.value)} className={inputClass}>
              <option value="">Choose a type…</option>
              {EQUIPMENT_CATEGORIES.map(c => {
                const inCat = targets.filter(t => t.category === c.key);
                if (!inCat.length) return null;
                return (
                  <optgroup key={c.key} label={c.label}>
                    {inCat.map(t => <option key={t.type_key} value={t.type_key}>{t.label}</option>)}
                  </optgroup>
                );
              })}
            </select>
          </div>
        </>
      ) : (
        <p className="text-sm text-neutral-700">Nothing uses this type. It will be removed from the type list.</p>
      )}
      {error && <p className="text-sm text-red-600">{error}</p>}
    </Modal>
  );
}

function builtinCategory(type: ManagedEquipmentType, all: ManagedEquipmentType[]): EquipmentCategory {
  return all.find(t => t.is_builtin && t.type_key === type.type_key)?.category ?? type.category;
}
